import { useCallback, useState } from 'react';
import { apiFetch, apiWrite } from '../../lib/api-client.js';

export const SCORECARD_METRICS = [
  { key: 'service', label: 'Service', description: 'Overall service score' },
  { key: 'door_tags', label: 'Door Tags', description: 'Door tag compliance' },
  { key: 'signature', label: 'Signature', description: 'Signature capture rate' },
  { key: 'pickups', label: 'Pickups', description: 'Pickup performance' },
  { key: 'vedr', label: 'VEDR', description: 'Vehicle event data recorder score' },
  { key: 'accidents', label: 'Accidents', description: 'Accident/incident score' },
  { key: 'ryde', label: 'RYDE', description: 'RYDE compliance score' },
  { key: 'ppod', label: 'PPOD', description: 'PPOD compliance score' },
  { key: 'attendance', label: 'Attendance', description: 'Attendance metric' },
] as const;

export type MetricKey = typeof SCORECARD_METRICS[number]['key'];

export interface ScorecardEntry {
  id?: string | number;
  employee_id?: string | number;
  score_date?: string;
  total_weighted_score?: number;
  tier?: string;
  notes?: string;
  [key: string]: unknown;
}

interface ThresholdEntry {
  metric_key: string;
  display_name?: string;
  weight?: number;
  threshold_excellent?: number;
  threshold_good?: number;
  threshold_acceptable?: number;
  is_active?: boolean;
  display_order?: number;
}

interface CreateScorecardResponse {
  scorecard?: ScorecardEntry;
  error?: string;
}

interface ThresholdsResponse {
  thresholds?: ThresholdEntry[];
  error?: string;
}

export function useDailyEntryScorecard() {
  const [thresholds, setThresholds] = useState<ThresholdEntry[]>([]);
  const [thresholdsLoaded, setThresholdsLoaded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<ScorecardEntry | null>(null);

  const loadThresholds = useCallback(async () => {
    if (thresholdsLoaded) return;
    try {
      const data = await apiFetch<ThresholdsResponse>('/api/scorecard/thresholds');
      if (data.thresholds) {
        setThresholds(data.thresholds);
      }
      setThresholdsLoaded(true);
    } catch {
      // Non-fatal — form still works without threshold display
    }
  }, [thresholdsLoaded]);

  async function createScorecard(
    employeeId: string | number,
    scoreDate: string,
    metrics: Partial<Record<MetricKey, number | null>>,
    notes?: string,
  ): Promise<boolean> {
    setSubmitting(true);
    setError(null);

    try {
      const body: Record<string, unknown> = {
        employee_id: employeeId,
        score_date: scoreDate,
        notes: notes || null,
      };
      for (const [key, value] of Object.entries(metrics)) {
        if (value != null) body[key] = value;
      }

      const { ok, data } = await apiWrite<CreateScorecardResponse>('/api/scorecard', 'POST', body);

      if (!ok || data.error) {
        setError(data.error || 'Failed to create scorecard');
        return false;
      }

      setLastResult(data.scorecard || null);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Connection error');
      return false;
    } finally {
      setSubmitting(false);
    }
  }

  async function updateScorecard(
    scorecardId: string | number,
    metrics: Partial<Record<MetricKey, number | null>>,
    notes?: string,
  ): Promise<boolean> {
    setSubmitting(true);
    setError(null);

    try {
      const body: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(metrics)) {
        if (value !== undefined) body[key] = value;
      }
      if (notes !== undefined) body.notes = notes;

      const { ok, data } = await apiWrite<CreateScorecardResponse>(`/api/scorecard/${scorecardId}`, 'PATCH', body);

      if (!ok || data.error) {
        setError(data.error || 'Failed to update scorecard');
        return false;
      }

      setLastResult(data.scorecard || null);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Connection error');
      return false;
    } finally {
      setSubmitting(false);
    }
  }

  return {
    thresholds,
    loadThresholds,
    createScorecard,
    updateScorecard,
    submitting,
    error,
    lastResult,
    clearError: () => setError(null),
    clearResult: () => setLastResult(null),
  };
}
